import { Plus, Trash2 } from "lucide-react";
import { FieldLabel } from "@/components/FieldLabel";
import { Textarea } from "@/components/Textarea";
import { NormalButton } from "@/components/NormalButton";
import { Input } from "@/components/Input";
import {
  MAX_STAR_STORIES,
  STAR_PARTS,
  StarPortfolioStory,
  emptyStarStory,
  isStarStoryEmpty,
  missingStarParts,
  starStoryLengthErrors,
} from "@/utils/starPortfolio";

interface StarPortfolioFieldProps {
  value: StarPortfolioStory[];
  onChange: (stories: StarPortfolioStory[]) => void;
  /** Form-level message, shown under the last story. */
  error?: string;
  /**
   * True once the user has tried to submit. Each required part left empty is
   * then flagged on its own (red border + "<Part> is required"), so
   * scrollToFirstError can land the cursor on the exact box to fill in.
   * Before that, only the summary line under a started story shows.
   */
  showErrors?: boolean;
}

/**
 * Repeatable S.T.A.R. story field (1-3 entries), four labelled parts each.
 *
 * It used to be one textarea per story with "Situation: … Task: …" as
 * placeholder text, which meant the platform held stories whose parts it could
 * not tell apart — nothing could render them section by section or reuse them.
 * The four parts are separate fields here so they can be stored as the four
 * columns `stars` has always had.
 *
 * Shared by the Expert Application wizard's Contribution step and the
 * advisor-signup preference form (both via AdvisorPreferenceForm), so the two
 * collect this identically. The profile's S.T.A.R. card edits through
 * StarSectionCard instead.
 */
export const StarPortfolioField = ({
  value,
  onChange,
  error,
  showErrors = false,
}: StarPortfolioFieldProps) => {
  const stories = value.length > 0 ? value : [emptyStarStory()];

  const updateStory = (index: number, patch: Partial<StarPortfolioStory>) => {
    onChange(stories.map((story, i) => (i === index ? { ...story, ...patch } : story)));
  };

  const addStory = () => {
    if (stories.length >= MAX_STAR_STORIES) return;
    onChange([...stories, emptyStarStory()]);
  };

  const removeStory = (index: number) => {
    onChange(stories.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-4">
      {stories.map((story, index) => {
        // Only flag a part as missing once the user has started this story --
        // an untouched optional second story is not an error.
        const missing = missingStarParts(story);
        const isLast = index === stories.length - 1;
        // At least one story is required, so with nothing written at all the
        // FIRST story's parts are what is missing.
        const noStoryYet = stories.every(isStarStoryEmpty);
        const flagParts = showErrors && (missing.length > 0 || (index === 0 && noStoryYet && !!error));
        // Shown as soon as the text goes over, not only after a submit — the
        // user should find out while typing, not when the save fails.
        const tooLong = starStoryLengthErrors(story);

        return (
          <div
            key={index}
            className="flex flex-col gap-3 rounded-[12px] border border-secondary-200 p-4 dark:border-surface-border"
          >
            <div className="flex items-center justify-between">
              <FieldLabel required={index === 0}>
                {index === 0 ? "S.T.A.R. Portfolio" : `S.T.A.R. #${index + 1}`}
              </FieldLabel>
              {stories.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStory(index)}
                  className="flex-shrink-0 text-theme-text-muted hover:text-error-500"
                  aria-label={`Remove story ${index + 1}`}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>

            {index === 0 && (
              <p className="-mt-1 text-12 leading-4 text-theme-text-secondary">
                Share 1–3 stories that best demonstrate your impact as an operator. Fill in all four
                parts of each one.
              </p>
            )}

            <Input
              id={`starPortfolio-${index}-title`}
              label="Give this S.T.A.R. a name"
              optional
              placeholder="e.g. Turning around retention at Acme"
              value={story.title ?? ""}
              onChange={(e) => updateStory(index, { title: e.target.value })}
              error={tooLong.title}
            />

            {STAR_PARTS.map(({ key, label, hint, placeholder }) => (
              <div key={key} data-field className="flex flex-col gap-1">
                <FieldLabel required htmlFor={`starPortfolio-${index}-${key}`}>
                  {label}
                </FieldLabel>
                <p className="-mt-0.5 text-12 leading-4 text-theme-text-secondary">{hint}</p>
                <Textarea
                  id={`starPortfolio-${index}-${key}`}
                  rows={2}
                  maxRows={8}
                  richText
                  placeholder={placeholder}
                  value={story[key]}
                  onChange={(e) => updateStory(index, { [key]: e.target.value })}
                  error={
                    flagParts && !story[key].trim() ? `${label} is required` : tooLong[key]
                  }
                />
              </div>
            ))}

            {missing.length > 0 && (
              <p className="text-12 text-error-500">
                Please complete {missing.join(", ")} for this story, or remove it.
              </p>
            )}
            {isLast && error && <p className="text-12 text-error-500">{error}</p>}
          </div>
        );
      })}

      {stories.length < MAX_STAR_STORIES && (
        <NormalButton
          type="button"
          onClick={addStory}
          leftIcon={<Plus size={13} />}
          bgColor="bg-transparent"
          borderColor="border-secondary-200 dark:border-surface-border"
          textColor="text-theme-text-heading"
          hoverBgColor="hover:bg-secondary-100 dark:hover:bg-surface-card-inner"
          size="sm"
          className="w-fit rounded-[10px]"
        >
          Add another star
        </NormalButton>
      )}
    </div>
  );
};
